import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { ipadLogsApi } from '_/services/api';
import { create } from 'zustand';

interface MediaPipeStore {
  landmarker: FaceLandmarker | null;
  loading: boolean;
  error: string | null;
  recoveryAttempts: number;
  useCpuFallback: boolean;
  init: () => Promise<void>;
  reinitialize: (reason: string) => Promise<void>;
  detect: (video: HTMLVideoElement | HTMLCanvasElement) => any;
}

const MAX_RECOVERY_ATTEMPTS = 3;

function checkWebGL2Support(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    return Boolean(gl);
  } catch {
    return false;
  }
}

export const useMediaPipeStore = create<MediaPipeStore>((set, get) => ({
  landmarker: null,
  loading: false,
  error: null,
  recoveryAttempts: 0,
  useCpuFallback: false,

  init: async () => {
    if (get().landmarker || get().loading) return;
    set({ loading: true, error: null });

    const wasmUrl = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm';

    const modelUrl =
      'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

    try {
      const vision = await FilesetResolver.forVisionTasks(wasmUrl);

      const hasWebGL2 = checkWebGL2Support();
      const shouldTryGPU = hasWebGL2 && !get().useCpuFallback;

      let landmarker: FaceLandmarker | null = null;
      let delegateUsed: 'GPU' | 'CPU' = 'CPU';

      if (shouldTryGPU) {
        try {
          let canvas: HTMLCanvasElement | undefined;
          if (typeof document !== 'undefined') {
            canvas = document.createElement('canvas');
            canvas.addEventListener('webglcontextlost', (event) => {
              ipadLogsApi.insert({
                type: '[MediaPipe] webglcontextlost',
                message: `Contexto WebGL perdido em runtime. Alternando para CPU. statusMessage: ${
                  (event as any)?.statusMessage ?? 'n/a'
                }`,
              });
              set({ useCpuFallback: true });
              get().reinitialize('webglcontextlost');
            });
            canvas.addEventListener('webglcontextrestored', () => {
              ipadLogsApi.insert({
                type: '[MediaPipe] webglcontextrestored',
                message: 'Contexto WebGL restaurado pelo navegador',
              });
            });
          }

          landmarker = await FaceLandmarker.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: modelUrl,
              delegate: 'GPU',
            },
            ...(canvas ? { canvas } : {}),
            runningMode: 'VIDEO',
            numFaces: 1,
            outputFaceBlendshapes: true,
          });

          delegateUsed = 'GPU';
        } catch (gpuError) {
          delegateUsed = 'CPU';
          set({ useCpuFallback: true });
          ipadLogsApi.insert({
            type: '[MediaPipe] GPU delegate failed, falling back to CPU',
            message: gpuError instanceof Error ? gpuError.message : 'Erro desconhecido',
          });
          landmarker = null;
        }
      }

      if (!landmarker) {
        delegateUsed = 'CPU';
        landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: modelUrl,
            delegate: 'CPU',
          },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFaceBlendshapes: true,
        });
      }

      ipadLogsApi.insert({
        type: '[MediaPipe] initialized',
        message: `FaceLandmarker inicializado com sucesso. delegate: ${delegateUsed} (WebGL2: ${hasWebGL2})`,
      });

      set({ landmarker, loading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      ipadLogsApi.insert({
        type: '[MediaPipe] Initialization error',
        message: errorMessage,
      });
      set({
        loading: false,
        error: errorMessage,
      });
    }
  },

  reinitialize: async (reason: string) => {
    const { loading, landmarker, recoveryAttempts } = get();
    if (loading) return;

    if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      ipadLogsApi.insert({
        type: '[MediaPipe] recovery limit reached',
        message: `Reinicialização abortada após ${recoveryAttempts} tentativas. reason: ${reason}`,
      });
      return;
    }

    set({ recoveryAttempts: recoveryAttempts + 1, useCpuFallback: true });
    ipadLogsApi.insert({
      type: '[MediaPipe] reinitializing',
      message: `Recriando FaceLandmarker (modo CPU). reason: ${reason}; attempt: ${
        recoveryAttempts + 1
      }/${MAX_RECOVERY_ATTEMPTS}`,
    });

    try {
      landmarker?.close();
    } catch {}

    set({ landmarker: null });
    await get().init();
  },

  detect: (video) => {
    const landmarker = get().landmarker;
    if (!landmarker) return null;
    return landmarker.detectForVideo(video as HTMLVideoElement, performance.now());
  },
}));
